import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  getGridNumericOperators,
  getGridStringOperators
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDialogs } from '../hooks/useDialogs/useDialogs';
import useNotifications from '../hooks/useNotifications/useNotifications';
import {
  deleteOne as deleteGasStation,
  type GasStation,
} from '../data/gasstations';
import PageContainer from './PageContainer';
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { getGasStationsFirebase } from "../data/firebaseGasStations";
import { db } from "../firebase";
const INITIAL_PAGE_SIZE = 10;

const customColumns: GridColDef[] = [
  {
    field: 'price',
    headerName: 'Price',
    width: 120,
    filterOperators: getGridStringOperators().filter(
      (op) => op.value === 'equals'
    ),
  },
  {
    field: 'shopName',
    headerName: 'Shop Name',
    width: 160,
    filterOperators: getGridStringOperators().filter(
      (op) => op.value === 'equals' // only keep "contains"
    ),
  },
];

export default function GasStationList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );
  const [sortModel, setSortModel] = React.useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = React.useState<{
    rows: GasStation[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = React.useCallback(async () => {
  setError(null);
  setIsLoading(true);

  try {
    const sortField = sortModel.length > 0 ? sortModel[0].field : undefined;
    const sortDirection = sortModel.length > 0 ? sortModel[0].sort : 'asc';

    const items = await getGasStationsFirebase(db,sortField, sortDirection as 'asc' | 'desc', filterModel);

    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    const paginated = items.slice(start, end);

    setRowsState({
      rows: paginated,
      rowCount: items.length,
    });
  } catch (listDataError) {
    setError(listDataError as Error);
  }

  setIsLoading(false);
}, [paginationModel, sortModel, filterModel]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = React.useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/gasstations/${row.id}`);
    },
    [navigate],
  );

  const handleCreateClick = React.useCallback(() => {
    navigate('/gasstations/new');
  }, [navigate]);

  const handleRowEdit = React.useCallback(
    (station: GasStation) => () => {
      navigate(`/gasstations/${station.id}/edit`);
    },
    [navigate],
  );

  const handleRowDelete = React.useCallback(
    (station: GasStation) => async () => {
      const confirmed = await dialogs.confirm(
        `Do you wish to delete ${station.shopName}?`,
        {
          title: `Delete gas station?`,
          severity: 'error',
          okText: 'Delete',
          cancelText: 'Cancel',
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          await deleteGasStation(Number(station.id));

          notifications.show('Gas station deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
          loadData();
        } catch (deleteError) {
          notifications.show(
            `Failed to delete gas station. Reason:' ${(deleteError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
        setIsLoading(false);
      }
    },
    [dialogs, notifications, loadData],
  );

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = React.useMemo<GridColDef[]>(
    () => [
      ...customColumns,
      // { field: 'id', headerName: 'ID', width: 70 },
      // { field: 'shopName', headerName: 'Shop Name', width: 160 },
      // { field: 'telephone', headerName: 'Telephone', width: 140 },
      // { field: 'price', headerName: 'Price', width: 120 },
      { field: 'address', headerName: 'Address', width: 220 },
      { field: 'latitude', headerName: 'Latitude', width: 120 },
      { field: 'longitude', headerName: 'Longitude', width: 120 },
      { field: 'productType', headerName: 'Product Type', width: 120 },
      {
        field: 'updateDate',
        headerName: 'Update Date',
        width: 180,
        valueFormatter: (value: any) => {
          if (!value) return '';
          const date = new Date(value as string);
          if (isNaN(date.getTime())) return '';
          // Format as YYYY-MM-DD HH:MM:SS in UTC
          return date.toISOString().replace('T', ' ').replace('Z', '');
        }
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 1,
        align: 'right',
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit-item"
            icon={<EditIcon />}
            label="Edit"
            onClick={handleRowEdit(row)}
          />,
          <GridActionsCellItem
            key="delete-item"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleRowDelete(row)}
          />,
        ],
      },
    ],
    [handleRowEdit, handleRowDelete],
  );

  const pageTitle = 'Gas Stations';
  const offsetLon = 0.0026; // Approximate correction for 100m eastward shift
  const offsetLat = 0; // No significant latitudinal offset mentioned

  function formattedDate(argstr: string): string {
    return new Date(argstr).toLocaleDateString("ja-JP", { 
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  }

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <DataGrid
            rows={rowsState.rows}
            rowCount={rowsState.rowCount}
            columns={columns}
            pagination
            sortingMode="server"
            filterMode="server"
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            loading={isLoading}
            initialState={initialState}
            showToolbar
            pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
            sx={{
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: 'transparent',
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: 'none',
                },
              [`& .${gridClasses.row}:hover`]: {
                cursor: 'pointer',
              },
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'circular-progress',
                noRowsVariant: 'circular-progress',
              },
              baseIconButton: {
                size: 'small',
              },
            }}
          />
        )}
      </Box>

      <Box>
        <h2>Gas Station Locations</h2>
        <MapContainer
          style={{ height: "400px", width: "100%" }}
          center={[43.1, 141.35]}
          zoom={11}
          maxBounds={[[85, -180], [-85, 180]]}
          maxBoundsViscosity={1.0}
          minZoom={2}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
          />

          {rowsState.rows
            .filter(e => e.latitude && e.longitude)
            .map((station, i) => (
              <Marker
                key={i}
                position={[station.latitude + offsetLat, station.longitude + offsetLon]}
                icon={L.divIcon({
                  className: "custom-div-icon",
                  html: `<div style="display: flex; align-items: center;">
                          <img src="https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png" style="width:25px;height:41px;"/>
                          <span style="background: white; padding: 2px 6px; border-radius: 4px; margin-left: 4px; font-size: 12px; border: 1px solid #888;">
                            ${formattedDate(station.updateDate)} : ${station.price} 
                          </span>
                        </div>`,
                  iconSize: [120, 41],
                  iconAnchor: [12, 41],
                })}
              />
            ))}
        </MapContainer>
      </Box>
    </PageContainer>
  );
}